import { isArray, isObject } from "../omnia";
import {
    ApiCropStyleUpdate,
    ApiDescriptionStyleUpdate,
    ApiGrassStyleUpdate,
    ApiPurposeStyleUpdate,
    ApiVarietyStyleUpdate,
    BasicStyle,
    CropStyle,
    DescriptionStyle,
    GrassStyle,
    PurposeStyle,
    State,
    VarietyStyle,
} from "./types";

export function resolveState<S>(prev: S, action: React.SetStateAction<S>): S {
    return typeof action === "function" ? (action as (value: S) => S)(prev) : action;
}

export function getUpdateRequest(state: State): ApiCropStyleUpdate[] {
    const { crops, selectedYears } = state;

    // If the years have changed, we must send an update for all visible crops so as to overwrite the other years.
    // If the years have not changed, we can reduce JSON payload by only sending changed crops.
    const cropsToUpdate = hasYearsChanged(state) ? crops : crops.filter((crop) => hasCropChanged(crop, true));

    return cropsToUpdate.map((x) => getCropUpdateRequest(x, selectedYears));
}

function getColorAndPattern({ color, pattern }: BasicStyle): Pick<BasicStyle, "color" | "pattern"> {
    return {
        color,
        // At time of writing, there are some records in the database where `{ color: null, pattern: any }`. In the previous version, these records were ignored.
        // This self-heals the database by saving `{ colour: null, pattern: any }` records as `{ colour: null, pattern: null }`, which will remove those records from the database if they exist.
        pattern: color ? pattern : null,
    };
}

function getCropUpdateRequest(cropStyle: CropStyle, years: number[]): ApiCropStyleUpdate {
    const { cropId, varieties, purposes, grasses, descriptions } = cropStyle;

    return {
        ...getColorAndPattern(cropStyle),
        cropId,
        years,
        varieties: varieties.map(getVarietyUpdateRequest),
        purposes: purposes.map(getPurposeUpdateRequest),
        grasses: grasses.map(getGrassUpdateRequest),
        descriptions: descriptions.map(getDescriptionUpdateRequest),
    };
}

function getGrassUpdateRequest(grassStyle: GrassStyle): ApiGrassStyleUpdate {
    const { grassManagement, grassCuts } = grassStyle;

    return {
        ...getColorAndPattern(grassStyle),
        grassManagement,
        grassCuts,
    };
}

function getPurposeUpdateRequest(purposeStyle: PurposeStyle): ApiPurposeStyleUpdate {
    const { purpose } = purposeStyle;

    return {
        ...getColorAndPattern(purposeStyle),
        purpose,
    };
}

function getVarietyUpdateRequest(varietyStyle: VarietyStyle): ApiVarietyStyleUpdate {
    const { varietyId } = varietyStyle;

    return {
        ...getColorAndPattern(varietyStyle),
        varietyId,
    };
}

function getDescriptionUpdateRequest(descriptionStyle: DescriptionStyle): ApiDescriptionStyleUpdate {
    const { description } = descriptionStyle;

    return {
        ...getColorAndPattern(descriptionStyle),
        description,
    };
}

/**
 * Checks if any of the crops have changes.
 * @param state The state object to check.
 * @returns TRUE if changed; otherwise, FALSE.
 */
export function hasStateChanged(state: State) {
    // If we've changed the years, then we must consider everything changed.
    if (hasYearsChanged(state)) {
        return true;
    }

    // ..otherwise, actually check for individual style changes.
    return hasStylesChanged(state);
}

export function hasStylesChanged(state: State) {
    return state.crops.some((crop) => hasCropChanged(crop, true));
}

export function hasYearsChanged({ initialYears, selectedYears }: State) {
    return !compareArrays(initialYears, selectedYears);
}

/**
 * Checks if the crop (or nested varieties/purposes) has changes.
 * @param crop The crop to check.
 * @param checkChildren Whether to also check for changes in nested variety or purpose rows.
 * @returns TRUE if changed; otherwise, FALSE.
 */
export function hasCropChanged(crop: CropStyle, checkChildren: boolean) {
    if (hasStyleChanged(crop)) {
        return true;
    }

    if (checkChildren) {
        if (crop.varieties.some(hasVarietyChanged)) {
            return true;
        }
        if (crop.purposes.some(hasPurposeChanged)) {
            return true;
        }
        if (crop.grasses.some(hasGrassChanged)) {
            return true;
        }
        if(crop.descriptions.some(hasDescriptionChanged)) {
            return true;
        }
    }

    return false;
}

/**
 * Checks if the "variety" style has changes.
 * @param varietyStyle The style to check.
 * @returns TRUE if changed; otherwise, FALSE.
 */
export function hasVarietyChanged(varietyStyle: VarietyStyle) {
    return hasStyleChanged(varietyStyle);
}

/**
 * Checks if the "purpose" style has changes.
 * @param purposeStyle The style to check.
 * @returns TRUE if changed; otherwise, FALSE.
 */
export function hasPurposeChanged(purposeStyle: PurposeStyle) {
    return hasStyleChanged(purposeStyle);
}

/**
 * Checks if the "grass" style has changes.
 * @param grassStyle The style to check.
 * @returns TRUE if changed; otherwise, FALSE.
 */
export function hasGrassChanged(grassStyle: GrassStyle) {
    return hasStyleChanged(grassStyle);
}

/**
 * Checks if the "description" style has changes.
 * @param descriptionStyle The style to check.
 * @returns TRUE if changed; otherwise, FALSE.
 */
export function hasDescriptionChanged(descriptionStyle: DescriptionStyle) {
    return hasStyleChanged(descriptionStyle);
}

/**
 * Checks if the style has changes.
 * @param style The style to check.
 * @returns TRUE if changed; otherwise, FALSE.
 */
export function hasStyleChanged(style: BasicStyle & { initialState: BasicStyle }) {
    const { initialState, ...currentState } = style;

    return !compareStyles(currentState, initialState);
}

/**
 * Compares two style objects, returns true if identical (same color and pattern)
 * @param x An object to compare.
 * @param y An object to compare.
 * @returns TRUE if identical; otherwise, FALSE.
 */
export function compareStyles(x: BasicStyle, y: BasicStyle) {
    return isObject(x) && isObject(y) && x.pattern === y.pattern && compareColors(x.color, y.color);
}

function compareColors(a: number[], b: number[]) {
    return (a === undefined && b === undefined) || compareArrays(a, b);
}

function compareArrays(a: number[], b: number[]) {
    return a === b || (isArray(a) && isArray(b) && a.length === b.length && a.every((c, i) => c === b[i]));
}
